import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils";
import { toast } from "sonner";
import { Crown, Users2, CalendarClock, CheckCircle2, XCircle, Search, Loader2, History } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { LeadStatusBadge, fmtLeadDate } from "@/components/LeadStatus";

// iter203 — Team → Lead Management. Activity tracking only: no money.
const Stat = ({ label, value, icon: Icon, tone, testid }) => (
  <div className={`rounded-xl border p-4 ${tone}`} data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider opacity-70">
      <Icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {label}
    </div>
    <div className="mt-2 text-3xl font-serif tabular-nums" data-testid={`${testid}-value`}>{value}</div>
  </div>
);

export const LeadTable = ({ leads, onRowClick, showLead = true, testPrefix }) => (
  <div className="overflow-x-auto border border-stone-200 rounded-xl bg-white">
    <table className="w-full text-sm">
      <thead className="bg-stone-50 text-stone-500 uppercase text-[10px] tracking-wider">
        <tr>
          <th className="text-left px-4 py-2">Couple / Client</th>
          <th className="text-left px-4 py-2">Event</th>
          <th className="text-left px-4 py-2">Event Date</th>
          <th className="text-left px-4 py-2">Venue</th>
          <th className="text-left px-4 py-2">Booking Status</th>
          {showLead && <th className="text-left px-4 py-2">Shoot Lead</th>}
          <th className="text-right px-4 py-2">Days</th>
        </tr>
      </thead>
      <tbody data-testid={`${testPrefix}-rows`}>
        {leads.length === 0 && (
          <tr><td colSpan={showLead ? 7 : 6} className="px-4 py-8 text-center text-stone-500" data-testid={`${testPrefix}-empty`}>
            No leads found.
          </td></tr>
        )}
        {leads.map((l) => (
          <tr
            key={`${l.booking_id}-${l.shoot_lead_staff_id}`}
            onClick={onRowClick ? () => onRowClick(l) : undefined}
            className={`border-t border-stone-100 transition-colors ${onRowClick ? "cursor-pointer hover:bg-rose-50/40" : ""}`}
            data-testid={`${testPrefix}-row-${l.booking_id}`}
          >
            <td className="px-4 py-2.5 font-semibold text-stone-800">{l.couple_name}</td>
            <td className="px-4 py-2.5 text-stone-600">{l.event || "—"}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-stone-600 whitespace-nowrap">{fmtLeadDate(l.event_date)}</td>
            <td className="px-4 py-2.5 text-stone-600">{l.location || "—"}</td>
            <td className="px-4 py-2.5">
              <div className="flex items-center gap-1.5">
                <LeadStatusBadge status={l.lead_status} testid={`${testPrefix}-status-${l.booking_id}`} />
                <span className="text-[10px] text-stone-400 font-mono">{l.booking_status}</span>
              </div>
            </td>
            {showLead && <td className="px-4 py-2.5 text-stone-700">{l.shoot_lead_name || "—"}</td>}
            <td className="px-4 py-2.5 text-right tabular-nums">{l.event_days}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

function StaffHistorySheet({ staff, leads, onClose }) {
  const [history, setHistory] = useState([]);
  useEffect(() => {
    if (!staff) return;
    api.get(`/team/lead-management/history?staff_id=${staff.staff_id}`)
      .then((r) => setHistory(r.data)).catch(() => setHistory([]));
  }, [staff]);
  const mine = leads.filter((l) => l.shoot_lead_staff_id === staff?.staff_id);
  return (
    <Sheet open={!!staff} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto bg-white" data-testid="lead-staff-sheet">
        <SheetHeader>
          <SheetTitle className="font-serif text-2xl" data-testid="lead-staff-sheet-name">{staff?.name}</SheetTitle>
        </SheetHeader>
        <p className="text-xs text-stone-500 mt-1">
          {staff?.total} leads (excl. cancelled) · {staff?.active} active · {staff?.completed} completed · {staff?.cancelled} cancelled
        </p>
        <div className="mt-5"><LeadTable leads={mine} showLead={false} testPrefix="lead-staff-sheet" /></div>
        <h3 className="mt-8 mb-2 text-xs font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1.5">
          <History className="w-3.5 h-3.5" /> Assignment history
        </h3>
        {history.length === 0 ? (
          <p className="text-sm text-stone-500" data-testid="lead-staff-history-empty">
            No assignment changes recorded yet. History is tracked from now on.
          </p>
        ) : (
          <ul className="space-y-2" data-testid="lead-staff-history">
            {history.map((h, i) => (
              <li key={i} className="text-sm border border-stone-200 rounded-lg px-3 py-2 bg-stone-50/50" data-testid={`lead-staff-history-${i}`}>
                <span className="font-semibold">{h.client_name}</span>
                <span className="text-stone-500"> — {h.from_name || "Unassigned"} → {h.to_name || "Unassigned"}</span>
                <span className="block text-[11px] text-stone-400 font-mono">{(h.changed_at || "").slice(0, 16).replace("T", " ")} · {h.changed_by}</span>
              </li>
            ))}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
}

const FIELD = "input h-9 text-sm";

export default function LeadManagement() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [staffF, setStaffF] = useState("");
  const [statusF, setStatusF] = useState("");
  const [eventF, setEventF] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [openStaff, setOpenStaff] = useState(null);
  const [showAllStaff, setShowAllStaff] = useState(false);

  useEffect(() => {
    api.get("/team/lead-management").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const leads = data?.leads || [];
  const eventTypes = useMemo(() => [...new Set(leads.map((l) => l.event).filter(Boolean))].sort(), [leads]);
  const filtered = useMemo(() => leads.filter((l) => {
    if (staffF && l.shoot_lead_staff_id !== staffF) return false;
    if (statusF && l.lead_status !== statusF) return false;
    if (eventF && l.event !== eventF) return false;
    if (from && (l.last_event_date || l.event_date) < from) return false;
    if (to && (!l.event_date || l.event_date > to)) return false;
    if (q && !`${l.couple_name} ${l.client_name}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [leads, staffF, statusF, eventF, from, to, q]);

  if (!data) return <div className="py-10 text-stone-500 flex items-center gap-2" data-testid="lead-mgmt-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading leads…</div>;
  const s = data.summary;
  return (
    <div className="space-y-8" data-testid="lead-mgmt">
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat testid="lead-stat-total" label="Bookings with a lead" value={s.total} icon={Crown} tone="bg-rose-50/60 border-rose-200 text-rose-900" />
        <Stat testid="lead-stat-staff" label="Staff handling leads" value={s.staff_handling} icon={Users2} tone="bg-stone-50 border-stone-200 text-stone-800" />
        <Stat testid="lead-stat-active" label="Upcoming / Active" value={s.active} icon={CalendarClock} tone="bg-sky-50 border-sky-200 text-sky-900" />
        <Stat testid="lead-stat-completed" label="Completed" value={s.completed} icon={CheckCircle2} tone="bg-emerald-50 border-emerald-200 text-emerald-900" />
        <Stat testid="lead-stat-cancelled" label="Cancelled" value={s.cancelled} icon={XCircle} tone="bg-stone-100 border-stone-300 text-stone-500" />
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs font-bold uppercase tracking-wider text-stone-500">Staff-wise lead performance</h2>
          <button
            onClick={() => setShowAllStaff((v) => !v)}
            className="text-xs font-semibold text-rose-700 hover:text-rose-900"
            data-testid="lead-staff-toggle-all"
          >
            {showAllStaff ? "Hide staff with no leads" : "Show all staff"}
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="lead-staff-grid">
          {data.staff.filter((st) => showAllStaff || st.total > 0 || st.cancelled > 0).map((st) => (
            <button
              key={st.staff_id}
              onClick={() => setOpenStaff(st)}
              className="text-left rounded-xl border border-stone-200 bg-white p-4 hover:border-rose-300 hover:-translate-y-0.5 transition-[transform,border-color] duration-200"
              data-testid={`lead-staff-card-${st.staff_id}`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-stone-800 truncate">{st.name}</span>
                <span className="text-2xl font-serif text-rose-800 tabular-nums" data-testid={`lead-staff-total-${st.staff_id}`}>{st.total}</span>
              </div>
              <div className="mt-2 flex gap-3 text-[11px] text-stone-500 font-mono">
                <span data-testid={`lead-staff-active-${st.staff_id}`}>{st.active} active</span>
                <span data-testid={`lead-staff-completed-${st.staff_id}`}>{st.completed} done</span>
                <span data-testid={`lead-staff-cancelled-${st.staff_id}`}>{st.cancelled} cancelled</span>
              </div>
            </button>
          ))}
        </div>
      </section>

      <section className="space-y-3">
        <h2 className="text-xs font-bold uppercase tracking-wider text-stone-500">All lead assignments</h2>
        <div className="flex flex-wrap items-center gap-2" data-testid="lead-filters">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400" />
            <input className={`${FIELD} !pl-8 !w-56`} placeholder="Search couple / client" value={q} onChange={(e) => setQ(e.target.value)} data-testid="lead-filter-search" />
          </div>
          <select className={`${FIELD} !w-44`} value={staffF} onChange={(e) => setStaffF(e.target.value)} data-testid="lead-filter-staff">
            <option value="">All staff</option>
            {data.staff.map((st) => <option key={st.staff_id} value={st.staff_id}>{st.name}</option>)}
          </select>
          <select className={`${FIELD} !w-36`} value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="lead-filter-status">
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select className={`${FIELD} !w-40`} value={eventF} onChange={(e) => setEventF(e.target.value)} data-testid="lead-filter-event">
            <option value="">All event types</option>
            {eventTypes.map((e) => <option key={e} value={e}>{e}</option>)}
          </select>
          <input type="date" className={`${FIELD} !w-40`} value={from} onChange={(e) => setFrom(e.target.value)} data-testid="lead-filter-from" />
          <span className="text-xs text-stone-400">to</span>
          <input type="date" className={`${FIELD} !w-40`} value={to} onChange={(e) => setTo(e.target.value)} data-testid="lead-filter-to" />
          <span className="text-xs text-stone-500 ml-auto" data-testid="lead-filter-count">{filtered.length} of {leads.length}</span>
        </div>
        <LeadTable leads={filtered} testPrefix="lead-table" />
      </section>

      <StaffHistorySheet staff={openStaff} leads={leads} onClose={() => setOpenStaff(null)} />
    </div>
  );
}
